import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Bell, Camera, Loader2, Mail, CheckCircle2, Church } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import MainLayout from "@/components/layout/MainLayout";
import { MyUnaSettingsTab } from "@/components/settings/MyUnaSettingsTab";
interface Profile {
  id: string;
  full_name: string | null;
  email: string;
  phone: string | null;
  timezone: string | null;
  avatar_url: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
}

const Settings = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Profile form state
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [timezone, setTimezone] = useState("America/New_York");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [country, setCountry] = useState("US");
  
  // Email change state
  const [newEmail, setNewEmail] = useState("");
  const [changingEmail, setChangingEmail] = useState(false);
  const [emailPendingConfirmation, setEmailPendingConfirmation] = useState(false);

  // Security form state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // Task notification preferences state (only columns that exist in notification_preferences table)
  const [taskNotificationPrefs, setTaskNotificationPrefs] = useState({
    email_on_task_assigned: true,
    email_day_before_due: true,
    email_on_task_created: true
  });
  const [taskNotificationLoading, setTaskNotificationLoading] = useState(true);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [userUnaId, setUserUnaId] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchProfile();
      loadTaskNotificationPrefs();
    }
  }, [user]);

  const loadTaskNotificationPrefs = async () => {
    if (!user?.id) return;
    
    try {
      // Get user's UNA (as Secretary Treasurer)
      const { data: unaData } = await supabase
        .from('unas')
        .select('id')
        .eq('treasury_secretary_id', user.id)
        .single();
      
      if (!unaData) {
        setTaskNotificationLoading(false);
        return;
      }
      
      setUserUnaId(unaData.id);
      
      // Get notification preferences from the TABLE
      const { data: prefs } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .eq('una_id', unaData.id)
        .single();
      
      if (prefs) {
        setTaskNotificationPrefs({
          email_on_task_assigned: prefs.email_on_task_assigned ?? true,
          email_day_before_due: prefs.email_day_before_due ?? true,
          email_on_task_created: prefs.email_on_task_created ?? true
        });
      }
    } catch (error) {
      console.error('Error loading task notification preferences:', error);
    } finally {
      setTaskNotificationLoading(false);
    }
  };

  const handleTaskNotificationSave = async () => {
    if (!user?.id || !userUnaId) {
      toast.error('No UNA found for your account');
      return;
    }
    
    setSavingNotifications(true);
    try {
      // Upsert to notification_preferences TABLE
      const { error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_id: user.id,
          una_id: userUnaId,
          ...taskNotificationPrefs,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,una_id'
        });
      
      if (error) throw error;
      
      toast.success('Task notification preferences saved!');
    } catch (error) {
      console.error('Error saving task notification preferences:', error);
      toast.error('Failed to save notification preferences');
    } finally {
      setSavingNotifications(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user?.id)
        .single();

      if (error) throw error;

      setProfile(data as Profile);
      setFullName(data.full_name || "");
      setPhone(data.phone || "");
      setTimezone(data.timezone || "America/New_York");
      setAddress(data.address || "");
      setCity(data.city || "");
      setState(data.state || "");
      setZip(data.zip || "");
      setCountry(data.country || "US");
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("File must be an image");
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split(".").pop();
      const filePath = `${user?.id}/avatar.${fileExt}`;

      // Delete old avatar if exists
      if (profile?.avatar_url) {
        const oldPath = profile.avatar_url.split("/").pop();
        if (oldPath) {
          await supabase.storage.from("avatars").remove([`${user?.id}/${oldPath}`]);
        }
      }

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("avatars")
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: publicUrl })
        .eq("id", user?.id);

      if (updateError) throw updateError;

      setProfile({ ...profile!, avatar_url: publicUrl });
      toast.success("Avatar updated successfully");
    } catch (error) {
      console.error("Error uploading avatar:", error);
      toast.error("Failed to upload avatar");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!profile?.avatar_url) return;

    try {
      const oldPath = profile.avatar_url.split("/").pop();
      if (oldPath) {
        await supabase.storage.from("avatars").remove([`${user?.id}/${oldPath}`]);
      }

      const { error } = await supabase
        .from("profiles")
        .update({ avatar_url: null })
        .eq("id", user?.id);

      if (error) throw error;

      setProfile({ ...profile, avatar_url: null });
      toast.success("Avatar removed");
    } catch (error) {
      console.error("Error removing avatar:", error);
      toast.error("Failed to remove avatar");
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: fullName,
          phone: phone,
          timezone: timezone,
          address: address,
          city: city,
          state: state,
          zip: zip,
          country: country,
        })
        .eq("id", user?.id);

      if (error) throw error;

      toast.success("Profile updated successfully");
      fetchProfile();
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!newEmail) {
      toast.error("Please enter a new email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (newEmail === profile?.email) {
      toast.error("New email must be different from current email");
      return;
    }

    setChangingEmail(true);
    try {
      const { error } = await supabase.auth.updateUser({
        email: newEmail,
      });

      if (error) throw error;

      setEmailPendingConfirmation(true);
      toast.success("Confirmation email sent! Check both your old and new email addresses.");
    } catch (error: any) {
      console.error("Error changing email:", error);
      toast.error(error.message || "Failed to change email");
    } finally {
      setChangingEmail(false);
    }
  };

  const getPasswordStrength = (password: string) => {
    if (password.length < 8) return { strength: "weak", color: "bg-red-500" };
    
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (hasUppercase && hasLowercase && hasNumbers && hasSpecial) {
      return { strength: "strong", color: "bg-green-500" };
    } else if (hasUppercase && hasLowercase && hasNumbers) {
      return { strength: "good", color: "bg-yellow-500" };
    }
    
    return { strength: "weak", color: "bg-red-500" };
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error("Please enter and confirm your new password");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast.success("Password changed successfully");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      console.error("Error changing password:", error);
      toast.error(error.message || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  const getInitials = () => {
    if (!profile?.full_name) return "U";
    const names = profile.full_name.split(" ");
    return names.map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const passwordStrength = getPasswordStrength(newPassword);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto py-8 px-4">
        <h1 className="text-3xl font-bold mb-8">Settings</h1>

        <Tabs defaultValue="profile" className="w-full">
          {/* Horizontally scrollable tabs on mobile */}
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 mb-8">
            <TabsList className="inline-flex w-auto md:grid md:w-full md:grid-cols-4 min-w-max md:min-w-0">
              <TabsTrigger value="profile" className="min-h-[44px] px-4">Profile</TabsTrigger>
              <TabsTrigger value="my-una" className="min-h-[44px] px-4">
                <Church className="h-4 w-4 mr-2" />
                My UNA
              </TabsTrigger>
              <TabsTrigger value="security" className="min-h-[44px] px-4">Security</TabsTrigger>
              <TabsTrigger value="notifications" className="min-h-[44px] px-4">Notifications</TabsTrigger>
            </TabsList>
          </div>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Profile Settings</CardTitle>
                <CardDescription>
                  Manage your profile information and how you appear to others
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Avatar Section */}
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <Avatar className="h-24 w-24">
                      <AvatarImage src={profile?.avatar_url || undefined} />
                      <AvatarFallback className="text-2xl">
                        {getInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <Button
                      size="icon"
                      variant="secondary"
                      className="absolute bottom-0 right-0 rounded-full h-8 w-8"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Camera className="h-4 w-4" />
                      )}
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleAvatarUpload}
                    />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      Upload a new avatar (max 5MB)
                    </p>
                    {profile?.avatar_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRemoveAvatar}
                      >
                        Remove Photo
                      </Button>
                    )}
                  </div>
                </div>

                {/* Profile Form */}
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="fullName">Full Name</Label>
                    <Input
                      id="fullName"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="flex gap-2">
                      <Input
                        id="email"
                        value={profile?.email || ""}
                        disabled
                        className="flex-1"
                      />
                    </div>
                    
                    {emailPendingConfirmation ? (
                      <div className="flex items-center gap-2 p-3 rounded-md bg-primary/10 border border-primary/20">
                        <CheckCircle2 className="h-4 w-4 text-primary" />
                        <p className="text-sm text-primary">
                          Confirmation email sent! Check both your old and new email addresses to complete the change.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Input
                            id="newEmail"
                            type="email"
                            placeholder="Enter new email address"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            className="flex-1"
                          />
                          <Button 
                            variant="outline" 
                            onClick={handleChangeEmail}
                            disabled={changingEmail || !newEmail}
                          >
                            {changingEmail ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Mail className="h-4 w-4 mr-2" />
                                Change
                              </>
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          You'll receive confirmation emails at both addresses
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="timezone">Timezone</Label>
                    <Select value={timezone} onValueChange={setTimezone}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                        <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                        <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                        <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                        <SelectItem value="America/Anchorage">Alaska Time (AKT)</SelectItem>
                        <SelectItem value="Pacific/Honolulu">Hawaii Time (HT)</SelectItem>
                        <SelectItem value="UTC">UTC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator className="my-4" />
                  
                  <div className="space-y-1">
                    <h3 className="font-medium">Residential Address</h3>
                    <p className="text-sm text-muted-foreground">
                      Your home address used for UNA documents and Center for Stewardship Observance
                    </p>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="address">Street Address</Label>
                    <Input
                      id="address"
                      placeholder="123 Main Street"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="grid gap-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        placeholder="City"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="state">State / Province</Label>
                      <Input
                        id="state"
                        placeholder="State"
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="zip">ZIP / Postal Code</Label>
                      <Input
                        id="zip"
                        placeholder="12345"
                        value={zip}
                        onChange={(e) => setZip(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      placeholder="US"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                    />
                  </div>
                </div>

                <Button onClick={saveProfile} disabled={saving}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Changes
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* My UNA Tab */}
          <TabsContent value="my-una">
            <MyUnaSettingsTab userId={user?.id || ""} unaId={userUnaId} />
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Set New Password</CardTitle>
                  <CardDescription>
                    Update your password to keep your account secure
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    {newPassword && (
                      <div className="space-y-2">
                        <div className="flex gap-1">
                          <div className={`h-2 flex-1 rounded ${passwordStrength.color}`} />
                          <div className={`h-2 flex-1 rounded ${passwordStrength.strength !== "weak" ? passwordStrength.color : "bg-muted"}`} />
                          <div className={`h-2 flex-1 rounded ${passwordStrength.strength === "strong" ? passwordStrength.color : "bg-muted"}`} />
                        </div>
                        <p className="text-xs text-muted-foreground capitalize">
                          Password strength: {passwordStrength.strength}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>

                  <Button onClick={handleChangePassword} disabled={saving}>
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Change Password
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Task Email Notifications
                </CardTitle>
                <CardDescription>
                  Configure when you receive email notifications about tasks
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {taskNotificationLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : !userUnaId ? (
                  <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg border">
                    <div className="text-2xl">ℹ️</div>
                    <div className="space-y-1">
                      <p className="font-medium">No UNA Found</p>
                      <p className="text-sm text-muted-foreground">
                        Create your UNA first to configure task notification preferences.
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Task Assignment</Label>
                        <p className="text-sm text-muted-foreground">
                          Receive an email when a task is assigned to you
                        </p>
                      </div>
                      <Switch
                        checked={taskNotificationPrefs.email_on_task_assigned}
                        onCheckedChange={(checked) => 
                          setTaskNotificationPrefs(prev => ({
                            ...prev,
                            email_on_task_assigned: checked
                          }))
                        }
                      />
                    </div>
                    
                    <Separator />
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Due Date Reminders</Label>
                        <p className="text-sm text-muted-foreground">
                          Receive an email one day before a task is due
                        </p>
                      </div>
                      <Switch
                        checked={taskNotificationPrefs.email_day_before_due}
                        onCheckedChange={(checked) => 
                          setTaskNotificationPrefs(prev => ({
                            ...prev,
                            email_day_before_due: checked
                          }))
                        }
                      />
                    </div>
                    
                    <Separator />
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Task Creation</Label>
                        <p className="text-sm text-muted-foreground">
                          Receive an email when a new task is created in your UNA
                        </p>
                      </div>
                      <Switch
                        checked={taskNotificationPrefs.email_on_task_created}
                        onCheckedChange={(checked) => 
                          setTaskNotificationPrefs(prev => ({
                            ...prev,
                            email_on_task_created: checked
                          }))
                        }
                      />
                    </div>
                  </>
                )}
              </CardContent>
              {userUnaId && !taskNotificationLoading && (
                <CardFooter>
                  <Button onClick={handleTaskNotificationSave} disabled={savingNotifications}>
                    {savingNotifications && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save Notification Preferences
                  </Button>
                </CardFooter>
              )}
            </Card>
          </TabsContent>


        </Tabs>
      </div>
    </MainLayout>
  );
};

export default Settings;
